import { c as _c } from "react/compiler-runtime";
import { useTranslation } from 'react-i18next';
import { useSuggestionsForGroup } from '../../store/suggestions';
import type { RuleGroup, Suggestion } from '../../store/types';
import ConditionLine, { diffConditions } from './primitives/conditions-code-diff';
import InlineEditPair from './primitives/inline-edit-pair';
import PatchCard from './primitives/patch-card';
import SetValueRow, { ValueChip } from './primitives/set-value-row';
import { diffField } from './utils/diff-field';
import { pickNoteKey } from './utils/note-key';
type Props = {
  rulesNodeId: string;
  group: RuleGroup;
};
const RulesGroupSuggestions = t0 => {
  const $ = _c(7);
  const {
    rulesNodeId,
    group
  } = t0;
  const suggestions = useSuggestionsForGroup(rulesNodeId, group.id);
  let t1;
  if ($[0] !== group || $[1] !== suggestions) {
    let t2;
    if ($[3] !== group) {
      t2 = s => <RulesGroupPatchCard key={s.id} suggestion={s} group={group} />;
      $[3] = group;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = suggestions.map(t2);
    $[0] = group;
    $[1] = suggestions;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <>{t1}</>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
const RulesGroupPatchCard = t0 => {
  const $ = _c(39);
  const {
    suggestion,
    group
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.suggestion.group"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const op = suggestion.operation;
  const isStale = !suggestion.canApply;
  if (op.kind === "remove_rules_group") {
    const t2 = suggestion.id;
    let t3;
    if ($[1] !== t) {
      t3 = t(_temp);
      $[1] = t;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] !== group.value) {
      t4 = <SetValueRow><ValueChip value={group.value} /></SetValueRow>;
      $[3] = group.value;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] !== group.value || $[6] !== t) {
      t5 = t(_temp2, {
        value: group.value
      });
      $[5] = group.value;
      $[6] = t;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] !== t5) {
      t6 = <p className="text-caption-3 text-tertiary">{t5}</p>;
      $[8] = t5;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    let t7;
    if ($[10] !== isStale || $[11] !== suggestion.id || $[12] !== t3 || $[13] !== t4 || $[14] !== t6) {
      t7 = <PatchCard suggestionId={t2} note={t3} isStale={isStale}>{t4}{t6}</PatchCard>;
      $[10] = isStale;
      $[11] = suggestion.id;
      $[12] = t3;
      $[13] = t4;
      $[14] = t6;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    return t7;
  }
  if (op.kind === "update_rules_group") {
    let t2;
    if ($[16] !== group.value || $[17] !== op.value) {
      t2 = diffField(group.value, op.value);
      $[16] = group.value;
      $[17] = op.value;
      $[18] = t2;
    } else {
      t2 = $[18];
    }
    const valueChange = t2;
    let t3;
    let t4;
    if ($[19] !== group.conditions || $[20] !== group.value || $[21] !== isStale || $[22] !== op.conditions || $[23] !== suggestion.id || $[24] !== t || $[25] !== valueChange) {
      t4 = Symbol.for("react.early_return_sentinel");
      bb0: {
        const suggestedExpressions = op.conditions?.map(_temp3);
        const rawDiff = suggestedExpressions !== undefined ? diffConditions(group.conditions.map(_temp4), suggestedExpressions) : null;
        const conditionDiff = rawDiff?.some(_temp5) ? rawDiff : null;
        const t5 = !!valueChange;
        const t6 = !!conditionDiff;
        let t7;
        if ($[28] !== t5 || $[29] !== t6) {
          t7 = pickNoteKey({
            refinedValue: t5,
            refinedConditions: t6
          }, "refinedBoth" as const);
          $[28] = t5;
          $[29] = t6;
          $[30] = t7;
        } else {
          t7 = $[30];
        }
        const noteKey = t7;
        if (!noteKey) {
          t4 = null;
          break bb0;
        }
        const t8 = suggestion.id;
        let t9;
        if ($[31] !== noteKey) {
          t9 = $_1 => $_1[noteKey];
          $[31] = noteKey;
          $[32] = t9;
        } else {
          t9 = $[32];
        }
        let t10;
        if ($[33] !== t || $[34] !== t9) {
          t10 = t(t9);
          $[33] = t;
          $[34] = t9;
          $[35] = t10;
        } else {
          t10 = $[35];
        }
        let t11;
        if ($[36] !== group.value || $[37] !== valueChange) {
          t11 = <SetValueRow>{valueChange ? <InlineEditPair current={valueChange.from} suggested={valueChange.to} /> : <ValueChip value={group.value} />}</SetValueRow>;
          $[36] = group.value;
          $[37] = valueChange;
          $[38] = t11;
        } else {
          t11 = $[38];
        }
        t3 = <PatchCard suggestionId={t8} note={t10} isStale={isStale} showDiffToggle={!!valueChange}>{t11}{conditionDiff && <div className="flex flex-col gap-1">{conditionDiff.map(_temp6)}</div>}</PatchCard>;
      }
      $[19] = group.conditions;
      $[20] = group.value;
      $[21] = isStale;
      $[22] = op.conditions;
      $[23] = suggestion.id;
      $[24] = t;
      $[25] = valueChange;
      $[26] = t3;
      $[27] = t4;
    } else {
      t3 = $[26];
      t4 = $[27];
    }
    if (t4 !== Symbol.for("react.early_return_sentinel")) {
      return t4;
    }
    return t3;
  }
  return null;
};
export default RulesGroupSuggestions;
function _temp($) {
  return $.remove;
}
function _temp2($_0) {
  return $_0.removeBody;
}
function _temp3(c) {
  return c.expression;
}
function _temp4(c_0) {
  return c_0.expression;
}
function _temp5(c_1) {
  return c_1.kind;
}
function _temp6(c_2, i) {
  return <ConditionLine key={`${c_2.kind ?? "keep"}-${c_2.expression}`} prefix={i === 0 ? "if" : "or"} expression={c_2.expression} kind={c_2.kind} />;
}